/** State-Machine + API-Verdrahtung des Optimierungen-Drawers (transient, MVP). */
import { useCallback, useState } from 'react'

import { generateChannelContent, recomputeChannel } from '@/lib/api/channel-content'

import { applyFieldToChannel } from './optimierungenActions'
import { type OptimierungField, patchField, snippetOf } from './optimierungenTypes'

export function useOptimierungen(
  productId: string,
  channelId: string,
  salesChannelId: string,
  initial: OptimierungField[],
) {
  const [fields, setFields] = useState<OptimierungField[]>(initial)

  const generate = useCallback(
    async (codes: string[]) => {
      if (!codes.length) return
      setFields((fs) => fs.map((f) => (codes.includes(f.attributeCode) ? { ...f, status: 'busy' } : f)))
      try {
        const res = await generateChannelContent(productId, channelId, { attribute_codes: codes })
        setFields((fs) =>
          fs.map((f) => {
            if (!codes.includes(f.attributeCode)) return f
            const sug = res.fields.find((x) => x.attribute_code === f.attributeCode)
            return sug ? { ...f, status: 'suggestion', suggestion: sug } : { ...f, status: 'error', errorKind: 'failed' }
          }),
        )
      } catch {
        setFields((fs) => fs.map((f) => (codes.includes(f.attributeCode) ? { ...f, status: 'error', errorKind: 'failed' } : f)))
      }
    },
    [productId, channelId],
  )

  const apply = useCallback(
    async (field: OptimierungField) => {
      if (!field.suggestion) return
      const r = await applyFieldToChannel(productId, salesChannelId, field.attributeCode, field.suggestion.suggestion)
      if (!r.ok) {
        setFields((fs) => patchField(fs, field.attributeCode, { status: 'error', errorKind: 'unconfigured', errorDetail: r.error }))
        return
      }
      setFields((fs) => patchField(fs, field.attributeCode, { status: 'applied', appliedSnippet: snippetOf(field.suggestion!.suggestion) }))
      recomputeChannel(productId, channelId).catch(() => {})
    },
    [productId, channelId, salesChannelId],
  )

  const discard = useCallback((code: string) => setFields((fs) => patchField(fs, code, { status: 'rest', suggestion: undefined })), [])

  const undo = useCallback(
    async (code: string) => {
      const field = fields.find((f) => f.attributeCode === code)
      if (!field?.suggestion) return
      const prev = field.suggestion.previous ?? (code === 'bullets' ? [] : '')
      await applyFieldToChannel(productId, salesChannelId, code, prev)
      setFields((fs) => patchField(fs, code, { status: 'suggestion' }))
      recomputeChannel(productId, channelId).catch(() => {})
    },
    [fields, productId, channelId, salesChannelId],
  )

  const bulkCodes = () => fields.filter((f) => !f.proactive && (f.status === 'rest' || f.status === 'error')).map((f) => f.attributeCode)

  return { fields, optimize: (c: string) => generate([c]), optimizeBulk: () => generate(bulkCodes()), apply, discard, undo }
}
